import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Grid
} from '@mui/material';
import { ChildDiscountConfig } from '@/types/trip';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/hooks/useI18n';

interface ChildDiscountConfigProps {
  config: ChildDiscountConfig;
  onUpdate: (config: ChildDiscountConfig) => void;
}

const ChildDiscountConfigComponent: React.FC<ChildDiscountConfigProps> = ({
  config,
  onUpdate
}) => {
  const [formData, setFormData] = useState<ChildDiscountConfig>(config);
  const { toast } = useToast();
  const { t } = useI18n();
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  // Debounced save function
  const debouncedSave = useCallback((data: ChildDiscountConfig) => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set a new timeout for saving
    saveTimeoutRef.current = setTimeout(() => {
      onUpdate(data);
      toast({
        title: t('common.save'),
        description: t('trip.childDiscount.updateSuccess'),
      });
    }, 1000); // 1 second delay
  }, [onUpdate, toast, t]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleEnabledChange = (enabled: boolean) => {
    const updatedConfig = { ...formData, enabled };
    setFormData(updatedConfig);
    debouncedSave(updatedConfig);
  };

  const handleMaxAgeChange = (maxAge: number) => {
    const updatedConfig = { ...formData, maxAge };
    setFormData(updatedConfig);
    debouncedSave(updatedConfig);
  };

  const handleDiscountAmountChange = (discountAmount: number) => {
    const updatedConfig = { ...formData, discountAmount };
    setFormData(updatedConfig);
    debouncedSave(updatedConfig);
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" component="h3">
            👶 {t('trip.childDiscount.title')}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled}
                onChange={(e) => handleEnabledChange(e.target.checked)}
                color="primary"
              />
            }
            label={formData.enabled ? t('common.enabled') : t('common.disabled')}
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('trip.childDiscount.description')}
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('trip.childDiscount.maxAge')}
              type="number"
              value={formData.maxAge}
              onChange={(e) => handleMaxAgeChange(parseInt(e.target.value) || 0)}
              disabled={!formData.enabled}
              inputProps={{ min: 0, max: 18 }}
              helperText={t('trip.childDiscount.maxAgeHelper')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('trip.childDiscount.discountAmount')}
              type="number"
              value={formData.discountAmount}
              onChange={(e) => handleDiscountAmountChange(parseFloat(e.target.value) || 0)}
              disabled={!formData.enabled}
              inputProps={{ min: 0, step: 0.01 }}
              helperText={t('trip.childDiscount.discountAmountHelper')}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ChildDiscountConfigComponent;
